import { LauncherGateStateSchema } from './launcher-gate.js';
import { ModpackDeploymentSchema } from './modpack.js';
import { WorldLayoutMoveSchema } from './world-layout.js';
import { WorldGenerationSchema, GameSettingsPatchSchema } from './world-settings.js';
import { PortsDeploymentSchema } from './ports.js';
import { SftpAgentSchemas } from './sftp.js';
import { UpdateAgentFields } from './version.js';
import { z } from 'zod';
import { DIAGNOSTIC_MAX_BYTES } from './diagnostics.js';
import { ClientPackSchema, ClientPackResultSchema } from './client-pack.js';
import { LaunchViewSchema } from './jvm.js';
import { FloodgateKeySchema } from './velocity.js';
import { BedrockDeploymentSchema } from './bedrock.js';
import {
  ServerSchema,
  ServerRecordSchema,
  ResourceSpecObjectSchema,
  SoftwareSpecSchema,
  ServerStateSchema,
} from './domain.js';
import { InstalledModSchema } from './mods.js';
import { AgentPolicySchema } from './maintenance.js';
import { PlayerActionSchema } from './players.js';

export const ServerMetadataSchema = z.object({
  software: SoftwareSpecSchema,
  mods: z.array(InstalledModSchema).optional(),
  modpackId: z.string().uuid().optional(),
  launch: LaunchViewSchema.optional(),
  server: ServerSchema.optional(),
  dataVersion: z.number().int().nonnegative().optional(),
  history: z.array(z.object({ at: z.string().datetime(), software: SoftwareSpecSchema })).default([]),
});
export type ServerMetadata = z.infer<typeof ServerMetadataSchema>;
export const MetricsSchema = z
  .object({
    at: z.string().datetime(),
    cpuPercent: z.number().nonnegative().nullable(),
    memoryBytes: z.number().nonnegative().nullable(),
    diskUsedBytes: z.number().nonnegative(),
    diskTotalBytes: z.number().nonnegative(),
    players: z.array(z.string()),
    playerCount: z.number().int().nonnegative(),
    maxPlayers: z.number().int().nonnegative().optional(),
    tps: z.number().nonnegative().nullable(),
    mspt: z.number().nonnegative().nullable(),
  })
  .strict();
export type AgentMetrics = z.infer<typeof MetricsSchema>;
import { RegenerateWorldSchema, LogScopeSchema } from './server-settings.js';
const envelope = { id: z.string().min(1), serverId: z.string().min(1) };
export const LinkMessageEnvelopeSchema = z.object({ type: z.string().min(1), serverId: envelope.serverId });
export const PanelMessageSchema = z.discriminatedUnion('type', [
  z.object({ ...envelope, type: z.literal('verify_client_pack'), pack: ClientPackSchema }).strict(),
  z.object({ ...envelope, type: z.literal('get_command_catalog') }).strip(),
  /** 手動で入れた Mod・プラグインの jar と、jar の中の名前・版 */
  z.object({ ...envelope, type: z.literal('scan_addons') }).strip(),
  z.object({ ...envelope, type: z.literal('launcher_gate'), state: LauncherGateStateSchema }).strict(),
  /** 補助プラグインに、いま動いているサーバーの補完を聞く（プラグインの引数も出る） */
  z.object({ ...envelope, type: z.literal('live_completions'), input: z.string().max(4096) }).strict(),
  ...SftpAgentSchemas,
  z.object({ ...envelope, type: z.literal('get_world_generation') }).strip(),
  z
    .object({
      ...envelope,
      type: z.literal('regenerate_world'),
      options: RegenerateWorldSchema,
      generation: WorldGenerationSchema,
    })
    .strict(),
  z.object({ ...envelope, type: z.literal('open_logs'), scope: LogScopeSchema }).strict(),
  z.object({ ...envelope, type: z.literal('read_logs'), handle: z.string().uuid() }).strict(),
  z.object({ ...envelope, type: z.literal('close_logs'), handle: z.string().uuid() }).strict(),
  z.object({ ...envelope, type: z.literal('get_launch') }).strip(),
  z.object({ ...envelope, type: z.literal('get_game_settings') }).strip(),
  z.object({ ...envelope, type: z.literal('change_game_settings'), settings: GameSettingsPatchSchema }).strict(),
  z.object({ ...envelope, type: z.literal('update_agent'), ...UpdateAgentFields }).strict(),
  z
    .object({
      ...envelope,
      type: z.literal('prepare'),
      server: ServerRecordSchema.strip()
        .extend({
          software: SoftwareSpecSchema.strip(),
          resources: ResourceSpecObjectSchema.strip(),
        })
        .transform((server) => ServerSchema.parse(server)),
      mods: z.array(InstalledModSchema).optional(),
      modpack: ModpackDeploymentSchema.optional(),
      worldGeneration: WorldGenerationSchema.optional(),
      initialProperties: z.record(z.string(), z.string()).optional(),
      policy: AgentPolicySchema.optional(),
    })
    .strip(),
  z.object({ ...envelope, type: z.literal('change_ports'), deployment: PortsDeploymentSchema }).strict(),
  z.object({ ...envelope, type: z.literal('cache_proxy_bedrock'), deployment: BedrockDeploymentSchema }).strict(),
  z
    .object({
      ...envelope,
      type: z.literal('install_proxy_bedrock'),
      deployment: BedrockDeploymentSchema,
      floodgateKey: FloodgateKeySchema,
    })
    .strict(),
  z.object({ ...envelope, type: z.literal('cache_bedrock'), deployment: BedrockDeploymentSchema }).strict(),
  z.object({ ...envelope, type: z.literal('install_bedrock'), deployment: BedrockDeploymentSchema }).strict(),
  ...(['start', 'stop', 'status', 'freeze', 'thaw', 'metadata', 'grow_data_disk', 'players'] as const).map((type) =>
    z.object({ ...envelope, type: z.literal(type) }).strip(),
  ),
  z.object({ ...envelope, type: z.literal('player_action'), action: PlayerActionSchema }).strict(),
  z.object({ ...envelope, type: z.literal('fs_list'), path: z.string().max(500) }).strict(),
  z.object({ ...envelope, type: z.literal('fs_read'), path: z.string().max(500) }).strict(),
  z
    .object({
      ...envelope,
      type: z.literal('fs_write'),
      path: z.string().max(500),
      content: z.string().max(1024 * 1024),
      expectedSha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/)
        .optional(),
    })
    .strict(),
  z.object({ ...envelope, type: z.literal('fs_mkdir'), path: z.string().max(500) }).strict(),
  z.object({ ...envelope, type: z.literal('fs_trash'), path: z.string().max(500) }).strict(),
  z
    .object({
      ...envelope,
      type: z.literal('fs_upload'),
      uploadId: z.string().uuid(),
      path: z.string().max(500),
      offset: z.number().int().min(0),
      data: z.string().max(700_000),
      final: z.boolean(),
      overwrite: z.boolean(),
    })
    .strict(),
  z.object({ ...envelope, type: z.literal('console'), command: z.string().min(1).max(4096) }).strict(),
  z.object({ ...envelope, type: z.literal('tail_logs'), tail: z.number().int().min(1).max(10000) }).strict(),
  z
    .object({
      ...envelope,
      type: z.literal('import'),
      source: z.string().min(1),
      destination: z.string().min(1),
      dryRun: z.boolean(),
    })
    .strict(),
]);
export type PanelMessage = z.infer<typeof PanelMessageSchema>;
export const AgentMessageSchema = z.discriminatedUnion('type', [
  z
    .object({
      serverId: envelope.serverId,
      type: z.literal('diagnostic'),
      text: z
        .string()
        .max(DIAGNOSTIC_MAX_BYTES)
        .refine((text) => new TextEncoder().encode(text).length <= DIAGNOSTIC_MAX_BYTES),
    })
    .strict(),
  z.object({ serverId: envelope.serverId, type: z.literal('world_layout_move'), move: WorldLayoutMoveSchema }).strict(),
  z
    .object({
      serverId: envelope.serverId,
      type: z.literal('hello'),
      version: z.string(),
      host: z.object({ platform: z.string(), hostname: z.string() }),
      /** VM の IPv4（edge の転送先に使う。Panel が公開用ネットワークの範囲か検査する） */
      addresses: z.array(z.ipv4()).max(16).optional(),
      metadata: ServerMetadataSchema.nullable(),
    })
    .strict(),
  /** 長い指示（遅い回線でのダウンロードなど）がまだ進んでいることを Panel に知らせる。Panel は待ち時間を延ばす */
  z.object({ ...envelope, type: z.literal('working') }).strict(),
  z.object({ ...envelope, type: z.literal('client_pack_progress'), logs: ClientPackResultSchema.shape.logs }).strict(),
  z.object({ serverId: envelope.serverId, type: z.literal('state'), state: ServerStateSchema }).strict(),
  z.object({ serverId: envelope.serverId, type: z.literal('log'), line: z.string().max(65536) }).strict(),
  z
    .object({
      serverId: envelope.serverId,
      type: z.literal('log_snapshot'),
      lines: z.array(z.string().max(65536)).max(2000),
    })
    .strict(),
  z.object({ serverId: envelope.serverId, type: z.literal('metrics'), metrics: MetricsSchema }).strict(),
  z
    .object({
      ...envelope,
      type: z.literal('result'),
      ok: z.boolean(),
      value: z.unknown().optional(),
      error: z.string().optional(),
    })
    .strict(),
]);
export type AgentMessage = z.infer<typeof AgentMessageSchema>;
export const MetricsQuerySchema = z.object({ range: z.enum(['1h', '24h', '7d']).default('1h') }).strict();
export const AgentPlanInputSchema = z
  .object({
    software: SoftwareSpecSchema,
    launch: LaunchViewSchema.optional(),
    proxied: z.boolean().default(false),
    data: z.string().optional(),
    runtime: z.string().optional(),
  })
  .strict();
export const AgentVersionTableSchema = z.array(
  z.object({ id: z.string().min(1), worldVersion: z.number().int().nonnegative() }).strict(),
);
export const AgentConfigSchema = z
  .object({
    panelUrl: z.string().url(),
    /** world: ワールド VM（既定）、edge: 入口 VM、database: 共有データベースの VM */
    role: z.enum(['world', 'edge', 'database']).default('world'),
    serverId: z.string().min(1),
    agentToken: z.string().min(1),
    dev: z.boolean().default(false),
    data: z.string().default('/data'),
    runtime: z.string().default('/srv/mc/runtime'),
    cache: z.string().default('/var/cache/mc'),
    javaRoot: z.string().default('/opt/java'),
    javaExecutable: z.string().optional(),
    velocitySecret: z.string().min(1).optional(),
    rconPort: z.number().int().min(1).max(65535).default(25575),
  })
  .strict();
